/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../../reducers';
import { HistoricalReportData } from '../../../../types/reportData';
import { DATE, formatDate } from '../../../../utils/date';
import { ChartProps, COLOUR1, COLOUR2, COLOUR3, totalEmissionsUtrCodes, UtrVariables } from './constants';
import {
  getDataAsNumber,
  getDataAsNumberOrUndefined,
  getDataAsNumberTableAverage,
  getDataAsNumberTableSum,
  getDataAsPercentageTableSum,
  getLatestEffectiveDate, sumByFilter,
} from './helpers';
import { getUnitForUtrCodes, getUnitWithNumberScale } from './unitText';

export type InsightsChartSelector = (state: RootState) => ChartProps | undefined;

export enum DataProcessor {
  Number = 'number',
  TableColumnSum = 'tableColumnSum',
  TableColumnsPercentage = 'tableColumnsPercentage'
}

export function processInsightsData(
  processor: DataProcessor,
  historicalReportData: HistoricalReportData[],
  variables?: UtrVariables
): number | undefined {
  if (!variables) {
    return undefined;
  }
  switch (processor) {
    case DataProcessor.TableColumnsPercentage:
      const latestReportData = getLatest(historicalReportData)?.reportData;
      const numerator = getDataAsNumber(latestReportData, variables['a'].code, variables['a'].valueListCode);
      const denominator = getDataAsNumber(latestReportData, variables['b'].code, variables['b'].valueListCode);
      return 100 * numerator / denominator;
    case DataProcessor.TableColumnSum:
      const reportData = getLatest(historicalReportData)?.reportData;
      if (!reportData) {
        return undefined;
      }
      return Object.values(variables)
        .map(variable => getDataAsNumberOrUndefined(reportData, variable.code, variable.valueListCode))
        .reduce<number | undefined>((acc, value) => {
          if (value !== undefined) {
            return (acc ?? 0) + value;
          }
          return acc;
        }, undefined);
    case DataProcessor.Number:
    default:
      return getDataAsNumberOrUndefined(getLatest(historicalReportData)?.reportData, variables['a'].code, variables['a'].valueListCode);
  }
}

export const getHistoricalReportData = (state: RootState): HistoricalReportData[] => {
  if (state.insightsData.loaded && Array.isArray(state.insightsData.data)) {
    return [...state.insightsData.data].reverse();
  }
  return [];
}

const getLatest = (historicalReportData: HistoricalReportData[]): HistoricalReportData | undefined => {
  return historicalReportData[historicalReportData.length - 1];
}

export const getSurveyId = (historicalReportData?: HistoricalReportData) => {
  const report = historicalReportData?.reportData.find(a => a.compositeData?.surveyId);
  return report?.compositeData?.surveyId;
}

export const getUtrv = (historicalReportData?: HistoricalReportData, utrCode?: string) => {
  return historicalReportData?.reportData.find(d => d.universalTracker.code === utrCode);
}

export const getLatestReportData = createSelector([getHistoricalReportData], (historicalReportData): HistoricalReportData | undefined => {
  return getLatest(historicalReportData);
});

export const calculateSumOfValues = (reportData: HistoricalReportData, variables: UtrVariables) => {
  return Object.values(variables).reduce((reportSum, variable) => {
    const value = getDataAsNumber(reportData.reportData, variable.code, variable.valueListCode);
    return reportSum + value;
  }, 0)
};

export const selectDataParisAgreement = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'survey/generic/paris-aligned-targets';
  const valueListCode = 'production_target';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      total: getDataAsNumber(row.reportData, utrCode, valueListCode)
    }
  })

  const chartData = [
    ['Period', 'Aligned'],
    ...reportData.map(row => [row.date, row.total])
  ];

  return {
    utrCodes: [utrCode],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    data: chartData,
    showNoData: !reportData.some(r => (r.total) > 0),
    vAxisTitle: title,
  }
});

const scopeOneCode = 'gri/2020/305-1/a';
const scopeTwoLocation = 'gri/2020/305-2/a';
const scopeTwoMarket = 'gri/2020/305-2/b';
const scopeThreeCode = 'gri/2020/305-3/a';
const scopeUtrCodes = [scopeOneCode, scopeTwoLocation, scopeTwoMarket, scopeThreeCode];


const getShowNoData = function (chartData: unknown[][]) {
  return !chartData.slice(1).some(r => Number(r[1]) > 0);
};

export const selectDataGHGEmissionsDonut = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const scope1 = getDataAsNumber(latestHistorical?.reportData, scopeOneCode);
  const scope2a = getDataAsNumber(latestHistorical?.reportData, scopeTwoLocation);
  const scope2b = getDataAsNumber(latestHistorical?.reportData, scopeTwoMarket);
  const scope3 = getDataAsNumber(latestHistorical?.reportData, scopeThreeCode);

  const scope2 = scope2a + scope2b;

  const chartData = [
    ['Scope', 'Emissions'],
    ['Scope 1', scope1],
    ['Scope 2', scope2],
    ['Scope 3', scope3],
  ];

  return {
    effectiveDate: latestHistorical?.effectiveDate,
    utrCodes: scopeUtrCodes,
    showNoData: getShowNoData(chartData),
    data: chartData,
  }
});

export const selectDataTotalEmissionsOverTime = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const reportData = historicalReportData.map(row => ({
    date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
    emissions: calculateSumOfValues(row, totalEmissionsUtrCodes)
  }));

  const unitInfo = getUnitForUtrCodes(historicalReportData, totalEmissionsUtrCodes)
  const title = getUnitWithNumberScale(unitInfo)

  const chartData = [
    ['Period', 'Emissions'],
    ...reportData.map(row => [row.date, row.emissions]) ?? []
  ];

  return {
    utrCodes: scopeUtrCodes,
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.emissions > 0),
    data: chartData,
    vAxisTitle: title,
  }
});

export const selectDataGHGEmissionsPercentage = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const reportData = historicalReportData.map(row => {
    const scope1 = getDataAsNumber(row.reportData, scopeOneCode);
    const scope2a = getDataAsNumber(row.reportData, scopeTwoLocation);
    const scope2b = getDataAsNumber(row.reportData, scopeTwoMarket);
    const scope3 = getDataAsNumber(row.reportData, scopeThreeCode);
    const total = scope1 + scope2a + scope2b + scope3;
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      scope1: 100 * scope1 / total,
      scope2: 100 * (scope2a + scope2b) / total,
      scope3: 100 * scope3 / total,
    }
  });

  const chartData = [
    ['Period', 'Scope 1', 'Scope 2', 'Scope 3'],
    ...reportData.map(row => (
      [row.date, row.scope1, row.scope2, row.scope3]
    )) ?? []
  ];

  return {
    utrCodes: scopeUtrCodes,
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.scope1 + r.scope2 + r.scope3) > 0),
    data: chartData
  }
});

export const selectDataGHGEmissions = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: scopeOneCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    const scope1 = getDataAsNumber(row.reportData, scopeOneCode);
    const scope2a = getDataAsNumber(row.reportData, scopeTwoLocation);
    const scope2b = getDataAsNumber(row.reportData, scopeTwoMarket);
    const scope3 = getDataAsNumber(row.reportData, scopeThreeCode);

    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      scope1: scope1,
      scope2: (scope2a + scope2b),
      scope3: scope3,
    }
  });

  const chartData = [
    ['Period', 'Scope 1', { role: 'annotation' }, 'Scope 2', { role: 'annotation' }, 'Scope 3', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.scope1, row.scope1, row.scope2, row.scope2, row.scope3, row.scope3]
    )) ?? []
  ];

  return {
    utrCodes: scopeUtrCodes,
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.scope1 + r.scope2 + r.scope3) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataCapexSpentOnSustEnergy = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const reportData = historicalReportData.map(row => ({
    date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
    total: getDataAsNumber(row.reportData, 'survey/sdg/11.1/pc-investment-energy-renewable-energy')
  })
  );

  const chartData = [
    ['Period', 'Aligned', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.total, row.total]
    )) ?? []
  ];

  return {
    utrCodes: [
      'survey/sdg/11.1/pc-investment-energy-renewable-energy',
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.total) > 0),
    data: chartData
  }
});

export const selectDataPlasticWasteRecycled = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const reportData = historicalReportData.map(row => ({
    date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
    total: getDataAsNumber(row.reportData, 'survey/sdg/14.1/pc-plastic-waste-recycled', 'pc_recycled')
  })
  );

  const chartData = [
    ['Period', 'Recycled'],
    ...reportData.map(row => (
      [row.date, row.total]
    )) ?? []
  ];

  return {
    utrCodes: [
      'survey/sdg/14.1/pc-plastic-waste-recycled'
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.total) > 0),
    data: chartData
  }
});

export const selectDataHazardousAndNonHazardous = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/306-3/a';
  const valueListCode = 'hazardous_waste';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    const hazardous_waste = getDataAsNumberTableSum(row.reportData, utrCode, valueListCode);
    const non_hazardous = getDataAsNumberTableSum(row.reportData, utrCode, 'non_hazardous');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      hazardous_waste: hazardous_waste,
      non_hazardous: non_hazardous,
    }
  });

  const chartData = [
    ['Period', 'Hazardous', { role: 'annotation' }, 'Non-Hazardous', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.hazardous_waste, row.hazardous_waste, row.non_hazardous, row.non_hazardous]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.hazardous_waste + r.non_hazardous) > 0),
    data: chartData,
    vAxisTitle: title,
  }
});

export const selectDataBoardDiversity = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const male = getDataAsNumber(latestHistorical?.reportData, 'gri/2020/405-1/a', 'male');
  const female = getDataAsNumber(latestHistorical?.reportData, 'gri/2020/405-1/a', 'female');

  const chartData = [
    ['Gender', 'Employees'],
    ['Male', male],
    ['Female', female],
  ];

  return {
    utrCodes: [
      'gri/2020/405-1/a'
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  }
});

export const selectDataEmployeeCompensationVersusHighest = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/102-38';
  const valueListCode = 'denominator_value';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } });
  const title = getUnitWithNumberScale(unitInfo);

  const reportData = historicalReportData.map(row => {
    const highestPay = getDataAsNumberTableAverage(row.reportData, utrCode, valueListCode);
    const averagePay = getDataAsNumberTableAverage(row.reportData, utrCode, 'numerator_value');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      highestPay: highestPay,
      averagePay: averagePay
    }
  });

  const chartData = [
    ['Period', 'Highest paid', 'Average pay'],
    ...reportData.map(row => [row.date, row.highestPay, row.averagePay]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.highestPay + r.averagePay) > 0),
    data: chartData,
    vAxisTitle: title,
  }
});

export const selectDataSeniorStaffBonusBasedOnESG = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'survey/sdg/4.7/pc-bonuses-based-on-ees-topics';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode } });
  const title = getUnitWithNumberScale(unitInfo);

  const reportData = historicalReportData.map(row => {
    const pc = getDataAsNumber(row.reportData, utrCode);
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      pc: pc
    }
  });

  const chartData = [
    ['Period', '% of bonuses'],
    ...reportData.map(row => [row.date, row.pc]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.pc > 0),
    data: chartData,
    vAxisTitle: title,
  };
});

export const selectDataBoardDiversityGenderSplit = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/405-1/a';
  const valueListCode = 'male';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } });
  const title = getUnitWithNumberScale(unitInfo);

  const reportData = historicalReportData.map(row => {
    const male = getDataAsNumber(row.reportData, utrCode, valueListCode);
    const female = getDataAsNumber(row.reportData, utrCode, 'female');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      male: male,
      female: female
    }
  });

  const chartData = [
    ['Period', 'Male', { role: 'annotation' }, 'Female', { role: 'annotation' }],
    ...reportData.map(row => [row.date, row.male, row.male, row.female, row.female]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.male + r.female) > 0),
    data: chartData,
    vAxisTitle: title,
  };
});

export const selectDataBoardAntiCorruptionTrainingPc = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/205-2/d';
  const tableColumn = 'board_members_region';

  const reportData = historicalReportData.map(row => {
    const total = getDataAsNumberTableSum(row.reportData, utrCode, tableColumn);
    const trained = getDataAsNumberTableSum(row.reportData, utrCode, 'anti_corruption_training_number');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      pc: 100 * trained / total
    }
  });

  const chartData = [
    ['Period', 'Board with Anti-Corruption Training'],
    ...reportData.map(row => [row.date, row.pc]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.pc > 0),
    data: chartData,
    vAxisTitle: '%'
  };
});

export const selectDataOperationsInBreachPc = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'survey/sdg/10.5/pc-operations-break-financial-regulations';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode } });
  const title = getUnitWithNumberScale(unitInfo);

  const reportData = historicalReportData.map(row => {
    const pc = getDataAsNumber(row.reportData, utrCode);
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      pc: pc
    }
  });

  const chartData = [
    ['Period', '% Operations in Breach'],
    ...reportData.map(row => [row.date, row.pc]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.pc > 0),
    data: chartData,
    vAxisTitle: title
  };
});

export const selectDataEmployeeNumbers = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const utrCode = 'gri/2020/102-7/a';
  const male = getDataAsNumber(latestHistorical?.reportData, utrCode, 'org_scale_employees_male');
  const female = getDataAsNumber(latestHistorical?.reportData, utrCode, 'org_scale_employees_female');

  const chartData = [
    ['Gender', 'Employees', { role: 'annotation' }, { role: 'style' }],
    ['Male', male, `Male | ${male}`, COLOUR3],
    ['Female', female, `Female | ${female}`, COLOUR2],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeGenderDiversity = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const utrCode = 'gri/2020/405-1/b';
  const male = getDataAsPercentageTableSum({ reportData: latestHistorical?.reportData, utrCode, tableColumn: 'employees_per_category', percentageColumn: 'male' });
  const female = getDataAsPercentageTableSum({ reportData: latestHistorical?.reportData, utrCode, tableColumn: 'employees_per_category', percentageColumn: 'female' });

  const chartData = [
    ['Gender', 'Employees', { role: 'annotation' }, { role: 'style' }],
    ['Male', male, `Male | ${male}`, COLOUR3],
    ['Female', female, `Female | ${female}`, COLOUR2],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeAgeDiversity = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const utrCode = 'gri/2020/405-1/b';
  const under_30 = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'under_30');
  const age_30_50 = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'age_30_50');
  const over_50 = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'over_50');

  const chartData = [
    ['Age', 'Employees', { role: 'annotation' }, { role: 'style' }],
    ['Under 30', under_30, `Under 30 | ${under_30}`, COLOUR3],
    ['30 to 50', age_30_50, `30 to 50 | ${age_30_50}`, COLOUR2],
    ['Over 50', over_50, `Over 50 | ${over_50}`, COLOUR1],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeGenderDiversityHistorical = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/405-1/b';

  const reportData = historicalReportData.map(row => {
    const male = getDataAsNumberTableSum(row.reportData, utrCode, 'male');
    const female = getDataAsNumberTableSum(row.reportData, utrCode, 'female');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      male: male,
      female: female,
    }
  });

  const chartData = [
    ['Period', 'Male', { role: 'annotation' }, 'Female', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.male, row.male, row.female, row.female]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeAgeDiversityHistorical = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/405-1/b';

  const reportData = historicalReportData.map(row => {
    const under_30 = getDataAsNumberTableSum(row.reportData, utrCode, 'under_30');
    const age_30_50 = getDataAsNumberTableSum(row.reportData, utrCode, 'age_30_50');
    const over_50 = getDataAsNumberTableSum(row.reportData, utrCode, 'over_50');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      under_30: under_30,
      age_30_50: age_30_50,
      over_50: over_50,
    }
  });

  const chartData = [
    ['Period', 'Under 30', { role: 'annotation' }, '30 to 50', { role: 'annotation' }, 'Over 50', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.under_30, row.under_30, row.age_30_50, row.age_30_50, row.over_50, row.over_50]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataAverageHoursTrainingPerGender = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const utrCode = 'gri/2020/404-1/a';
  const male = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'male');
  const female = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'female');

  const chartData = [
    ['Gender', 'Employees', { role: 'annotation' }, { role: 'style' }],
    ['Male', male, `Male | ${male}`, COLOUR3],
    ['Female', female, `Female | ${female}`, COLOUR2],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeTurnoverByAge = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const utrCode = 'gri/2020/401-1/b';
  const under_30 = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'under_30');
  const age_30_50 = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'age_30_50');
  const over_50 = getDataAsNumberTableSum(latestHistorical?.reportData, utrCode, 'over_50');

  const chartData = [
    ['Age', 'Employees', { role: 'annotation' }, { role: 'style' }],
    ['Under 30', under_30, `Under 30 | ${under_30}`, COLOUR3],
    ['30 to 50', age_30_50, `30 to 50 | ${age_30_50}`, COLOUR2],
    ['Over 50', over_50, `Over 50 | ${over_50}`, COLOUR1],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeTurnoverByGender = createSelector([getLatestReportData], (latestHistorical): ChartProps => {
  const utrCode = 'gri/2020/401-1/b';
  const baseFilter = {
    reportData: latestHistorical?.reportData,
    utrCode,
    tableColumn: 'total_turnover',
  }

  const male = sumByFilter({
    ...baseFilter,
    filterColumn: { code: 'gender', value: ['male1'] }
  });

  const female = sumByFilter({
    ...baseFilter,
    filterColumn: { code: 'gender', value: ['females1'] }
  });

  const chartData = [
    ['Gender', 'Employees', { role: 'annotation' }, { role: 'style' }],
    ['Male', male, `Male | ${male}`, COLOUR3],
    ['Female', female, `Female | ${female}`, COLOUR2],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeTurnoverByAgeHistorical = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/401-1/b';

  const reportData = historicalReportData.map(row => {
    const under_30 = getDataAsNumberTableSum(row.reportData, utrCode, 'under_30');
    const age_30_50 = getDataAsNumberTableSum(row.reportData, utrCode, 'age_30_50');
    const over_50 = getDataAsNumberTableSum(row.reportData, utrCode, 'over_50');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      under_30: under_30,
      age_30_50: age_30_50,
      over_50: over_50,
    }
  });

  const chartData = [
    ['Period', 'Under 30', { role: 'annotation' }, '30 to 50', { role: 'annotation' }, 'Over 50', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.under_30, row.under_30, row.age_30_50, row.age_30_50, row.over_50, row.over_50]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeTurnoverByGenderHistorical = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/401-1/b';

  const reportData = historicalReportData.map(row => {
    const baseFilter = {
      reportData: row.reportData,
      utrCode,
      tableColumn: 'total_turnover',
    }

    const male = sumByFilter({
      ...baseFilter,
      filterColumn: { code: 'gender', value: ['male1'] }
    });

    const female = sumByFilter({
      ...baseFilter,
      filterColumn: { code: 'gender', value: ['females1'] }
    });

    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      male: male,
      female: female,
    }
  });

  const chartData = [
    ['Period', 'Male', { role: 'annotation' }, 'Female', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.male, row.male, row.female, row.female]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: getShowNoData(chartData),
    data: chartData,
  };
});

export const selectDataEmployeeGenderSplitDonut = createSelector([getLatestReportData], (latestHistorical): ChartProps => {

  const utrCode = 'gri/2020/102-7/a';
  const male = getDataAsNumber(latestHistorical?.reportData, utrCode, 'org_scale_employees_male');
  const female = getDataAsNumber(latestHistorical?.reportData, utrCode, 'org_scale_employees_female');

  const chartData = [
    ['Gender', 'Employees'],
    ['Male', male],
    ['Female', female],
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: latestHistorical?.effectiveDate,
    showNoData: !chartData.some(r => Number(r[1]) > 0),
    data: chartData
  };
});

export const selectDataFemalePayMalePay = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'gri/2020/405-2/a';
  const valueListCode = 'denominator_male';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } });
  const title = getUnitWithNumberScale(unitInfo);

  const reportData = historicalReportData.map(row => {
    const male = getDataAsNumber(row.reportData, utrCode, valueListCode);
    const female = getDataAsNumber(row.reportData, utrCode, 'numerator_female');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      male: male,
      female: female,
    }
  });

  const chartData = [
    ['Period', 'Male', { role: 'annotation' }, 'Female', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.male, row.male, row.female, row.female]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.male + r.female) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataGenderAverageHoursTraining = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {
  const utrCode = 'gri/2020/404-1/a';
  const valueListCode = 'male';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } });
  const title = getUnitWithNumberScale(unitInfo);

  const reportData = historicalReportData.map(row => {
    const male = getDataAsNumber(row.reportData, utrCode, valueListCode);
    const female = getDataAsNumber(row.reportData, utrCode, 'female');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      male: male,
      female: female,
    }
  });

  const chartData = [
    ['Period', 'Male', { role: 'annotation' }, 'Female', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.male, row.male, row.female, row.female]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.male + r.female) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataEmployeeGenderSplit = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'gri/2020/102-7/a';
  const valueListCode = 'org_scale_employees_male';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    const male = getDataAsNumber(row.reportData, utrCode, valueListCode);
    const female = getDataAsNumber(row.reportData, utrCode, 'org_scale_employees_female');
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      male: male,
      female: female,
    }
  });

  const chartData = [
    ['Period', 'Male', { role: 'annotation' }, 'Female', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.male, row.male, row.female, row.female]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.male + r.female) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataTurnoverByGender = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'survey/sdg/5.1/ratio-employee-turnover';
  const valueListCode = 'ratio_turnover';
  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    const ratio = getDataAsNumber(row.reportData, utrCode, valueListCode);
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      ratio: ratio
    }
  });

  const chartData = [
    ['Period', 'Ratio'],
    ...reportData.map(row => [row.date, row.ratio]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.ratio) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataWorkforcePaidLivingWagePc = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'survey/generic/pc-company-workforce-paid-national-living-wage';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    const pc = getDataAsNumber(row.reportData, utrCode);
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      pc: pc,
    }
  });

  const chartData = [
    ['Period', 'Avg Male Pay', { role: 'annotation' }],
    ...reportData.map(row => (
      [row.date, row.pc, row.pc]
    )) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.pc) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataEnergyConsumption = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'gri/2020/302-1/e';
  const reportData = historicalReportData.map(row => ({
    date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
    consumption: getDataAsNumber(row.reportData, utrCode),
  }));

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const chartData = [
    ['Period', 'Consumption'],
    ...reportData.map(row => (
      [row.date, row.consumption]
    )) ?? []
  ];
  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.consumption) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataWaterConsumption = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'gri/2020/303-5/a';
  const reportData = historicalReportData.map(row => ({
    date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
    consumption: getDataAsNumber(row.reportData, utrCode),
  }));

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const chartData = [
    ['Period', 'Consumption'],
    ...reportData.map(row => (
      [row.date, row.consumption]
    )) ?? []
  ];
  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => (r.consumption) > 0),
    data: chartData,
    vAxisTitle: title
  }
});

export const selectDataExpenditureOnEmployeeHealth = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'unctad/2020/c.3.1';
  const valueListCode = 'percent_expenditure_hs';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    const expenditurePc = getDataAsNumber(row.reportData, utrCode, valueListCode);
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      expenditurePc: expenditurePc
    }
  });

  const chartData = [
    ['Period', 'Expenditure Percentage'],
    ...reportData.map(row => [row.date, row.expenditurePc]) ?? []
  ];

  return {
    utrCodes: [
      utrCode
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.expenditurePc > 0),
    data: chartData,
    vAxisTitle: title
  }
});


export const selectDataVolunteeringHours = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'survey/generic/volunteering-hours';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      donations: getDataAsNumber(row.reportData, utrCode)
    }
  });

  const chartData = [
    ['Period', 'Total volunteering hours'],
    ...reportData.map(row => [row.date, row.donations]) ?? []
  ];

  return {
    utrCodes: [
      utrCode,
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.donations > 0),
    data: chartData,
    vAxisTitle: title,
  }
});

export const selectDataTotalCharitableDonations = createSelector([getHistoricalReportData], (historicalReportData): ChartProps => {

  const utrCode = 'unctad/2020/a.3.2';
  const valueListCode = 'total_charity';

  const unitInfo = getUnitForUtrCodes(historicalReportData, { a: { code: utrCode, valueListCode } })
  const title = getUnitWithNumberScale(unitInfo)

  const reportData = historicalReportData.map(row => {
    return {
      date: formatDate(row.effectiveDate, DATE.MONTH_YEAR_SHORT, true),
      donations: getDataAsNumber(row.reportData, utrCode, valueListCode)
    }
  });

  const chartData = [
    ['Period', 'Total charitable donations'],
    ...reportData.map(row => [row.date, row.donations]) ?? []
  ];

  return {
    utrCodes: [
      utrCode,
    ],
    effectiveDate: getLatestEffectiveDate(historicalReportData),
    showNoData: !reportData.some(r => r.donations > 0),
    data: chartData,
    vAxisTitle: title,
  }
});

// used to store in the database and using another map to resolve it to the selector
export const insightChartSelectorsNames: { [key: string]: string } = {
  selectDataAverageHoursTrainingPerGender: 'selectDataAverageHoursTrainingPerGender',
  selectDataBoardAntiCorruptionTrainingPc: 'selectDataBoardAntiCorruptionTrainingPc',
  selectDataBoardDiversity: 'selectDataBoardDiversity',
  selectDataBoardDiversityGenderSplit: 'selectDataBoardDiversityGenderSplit',
  selectDataCapexSpentOnSustEnergy: 'selectDataCapexSpentOnSustEnergy',
  selectDataEmployeeAgeDiversity: 'selectDataEmployeeAgeDiversity',
  selectDataEmployeeAgeDiversityHistorical: 'selectDataEmployeeAgeDiversityHistorical',
  selectDataEmployeeCompensationVersusHighest: 'selectDataEmployeeCompensationVersusHighest',
  selectDataEmployeeGenderDiversity: 'selectDataEmployeeGenderDiversity',
  selectDataEmployeeGenderDiversityHistorical: 'selectDataEmployeeGenderDiversityHistorical',
  selectDataEmployeeGenderSplit: 'selectDataEmployeeGenderSplit',
  selectDataEmployeeGenderSplitDonut: 'selectDataEmployeeGenderSplitDonut',
  selectDataEmployeeNumbers: 'selectDataEmployeeNumbers',
  selectDataEmployeeTurnoverByAge: 'selectDataEmployeeTurnoverByAge',
  selectDataEmployeeTurnoverByAgeHistorical: 'selectDataEmployeeTurnoverByAgeHistorical',
  selectDataEmployeeTurnoverByGender: 'selectDataEmployeeTurnoverByGender',
  selectDataEmployeeTurnoverByGenderHistorical: 'selectDataEmployeeTurnoverByGenderHistorical',
  selectDataEnergyConsumption: 'selectDataEnergyConsumption',
  selectDataExpenditureOnEmployeeHealth: 'selectDataExpenditureOnEmployeeHealth',
  selectDataFemalePayMalePay: 'selectDataFemalePayMalePay',
  selectDataGHGEmissions: 'selectDataGHGEmissions',
  selectDataGHGEmissionsDonut: 'selectDataGHGEmissionsDonut',
  selectDataGHGEmissionsPercentage: 'selectDataGHGEmissionsPercentage',
  selectDataGenderAverageHoursTraining: 'selectDataGenderAverageHoursTraining',
  selectDataHazardousAndNonHazardous: 'selectDataHazardousAndNonHazardous',
  selectDataOperationsInBreachPc: 'selectDataOperationsInBreachPc',
  selectDataParisAgreement: 'selectDataParisAgreement',
  selectDataPlasticWasteRecycled: 'selectDataPlasticWasteRecycled',
  selectDataSeniorStaffBonusBasedOnESG: 'selectDataSeniorStaffBonusBasedOnESG',
  selectDataTotalCharitableDonations: 'selectDataTotalCharitableDonations',
  selectDataTotalEmissionsOverTime: 'selectDataTotalEmissionsOverTime',
  selectDataTurnoverByGender: 'selectDataTurnoverByGender',
  selectDataVolunteeringHours: 'selectDataVolunteeringHours',
  selectDataWaterConsumption: 'selectDataWaterConsumption',
  selectDataWorkforcePaidLivingWagePc: 'selectDataWorkforcePaidLivingWagePc'
}

export const insightChartSelectorsMap: { [key: string]: InsightsChartSelector } = {
  selectDataAverageHoursTrainingPerGender: selectDataAverageHoursTrainingPerGender,
  selectDataBoardAntiCorruptionTrainingPc: selectDataBoardAntiCorruptionTrainingPc,
  selectDataBoardDiversity: selectDataBoardDiversity,
  selectDataBoardDiversityGenderSplit: selectDataBoardDiversityGenderSplit,
  selectDataCapexSpentOnSustEnergy: selectDataCapexSpentOnSustEnergy,
  selectDataEmployeeAgeDiversity: selectDataEmployeeAgeDiversity,
  selectDataEmployeeAgeDiversityHistorical: selectDataEmployeeAgeDiversityHistorical,
  selectDataEmployeeCompensationVersusHighest: selectDataEmployeeCompensationVersusHighest,
  selectDataEmployeeGenderDiversity: selectDataEmployeeGenderDiversity,
  selectDataEmployeeGenderDiversityHistorical: selectDataEmployeeGenderDiversityHistorical,
  selectDataEmployeeGenderSplit: selectDataEmployeeGenderSplit,
  selectDataEmployeeGenderSplitDonut: selectDataEmployeeGenderSplitDonut,
  selectDataEmployeeNumbers: selectDataEmployeeNumbers,
  selectDataEmployeeTurnoverByAge: selectDataEmployeeTurnoverByAge,
  selectDataEmployeeTurnoverByAgeHistorical: selectDataEmployeeTurnoverByAgeHistorical,
  selectDataEmployeeTurnoverByGender: selectDataEmployeeTurnoverByGender,
  selectDataEmployeeTurnoverByGenderHistorical: selectDataEmployeeTurnoverByGenderHistorical,
  selectDataEnergyConsumption: selectDataEnergyConsumption,
  selectDataExpenditureOnEmployeeHealth: selectDataExpenditureOnEmployeeHealth,
  selectDataFemalePayMalePay: selectDataFemalePayMalePay,
  selectDataGHGEmissions: selectDataGHGEmissions,
  selectDataGHGEmissionsDonut: selectDataGHGEmissionsDonut,
  selectDataGHGEmissionsPercentage: selectDataGHGEmissionsPercentage,
  selectDataGenderAverageHoursTraining: selectDataGenderAverageHoursTraining,
  selectDataHazardousAndNonHazardous: selectDataHazardousAndNonHazardous,
  selectDataOperationsInBreachPc: selectDataOperationsInBreachPc,
  selectDataParisAgreement: selectDataParisAgreement,
  selectDataPlasticWasteRecycled: selectDataPlasticWasteRecycled,
  selectDataSeniorStaffBonusBasedOnESG: selectDataSeniorStaffBonusBasedOnESG,
  selectDataTotalCharitableDonations: selectDataTotalCharitableDonations,
  selectDataTotalEmissionsOverTime: selectDataTotalEmissionsOverTime,
  selectDataTurnoverByGender: selectDataTurnoverByGender,
  selectDataVolunteeringHours: selectDataVolunteeringHours,
  selectDataWaterConsumption: selectDataWaterConsumption,
  selectDataWorkforcePaidLivingWagePc: selectDataWorkforcePaidLivingWagePc
}
